import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shipmentsApi } from '../../api/shipments-api';
import { ConflictError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { canCancelShipment, type Shipment } from '../../domain/shipment';
import { bookingErrorMessage, isUnknownOutcome } from '../shipment-create/booking-errors';

const MAX_NOTE_LENGTH = 500;

/**
 * Cancels a booked shipment that was never sent, e.g. a label nobody used.
 * It stops being tracked and its units are left as they are. Cargonizer
 * isn't changed: unused consignments aren't charged.
 */
export function CancelShipmentAction({ shipment }: { shipment: Shipment }) {
  const queryClient = useQueryClient();
  const dialogId = useId();
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const refreshShipment = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['shipment', shipment.id] }),
      queryClient.invalidateQueries({ queryKey: ['shipments'] }),
    ]);

  const cancel = useMutation({
    mutationFn: () => shipmentsApi.cancelShipment(shipment.id, shipment.version, note),
    onSuccess: async () => {
      await refreshShipment();
    },
    onError: (err: unknown) => {
      if (isUnknownOutcome(err)) {
        setUnknownOutcome(true);
        return;
      }
      setError(
        err instanceof ConflictError
          ? 'This shipment changed after you opened it. Reload it and check before cancelling.'
          : bookingErrorMessage(err),
      );
    },
  });

  if (!canCancelShipment(shipment)) return null;

  return (
    <div className="cancel-shipment">
      <button
        type="button"
        onClick={() => {
          setError(null);
          setUnknownOutcome(false);
          setConfirming(true);
        }}
        disabled={cancel.isPending}
      >
        {cancel.isPending ? 'Cancelling…' : 'Cancel shipment'}
      </button>
      {error ? (
        <p role="alert" className="form-error">
          {error}
        </p>
      ) : null}
      {unknownOutcome ? (
        <PartialFailureNotice
          onCheckCurrentState={() => {
            setUnknownOutcome(false);
            void refreshShipment();
          }}
        />
      ) : null}
      {confirming ? (
        <div
          className="drawer-overlay centered-overlay"
          role="presentation"
          onClick={() => setConfirming(false)}
        >
          <div
            className="confirmation-dialog"
            role="alertdialog"
            aria-labelledby={`${dialogId}-title`}
            aria-describedby={`${dialogId}-body`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="drawer-heading">
              <h2 id={`${dialogId}-title`}>Cancel this shipment?</h2>
            </div>
            <div className="warning-callout" id={`${dialogId}-body`}>
              <p>
                Only cancel a shipment that was never sent. It stops being tracked, and its units
                stay as they are. Consignment {shipment.cargonizerConsignmentNumber || '(none)'} is
                left in Cargonizer; unused consignments aren’t charged.
              </p>
            </div>
            <label>
              Note (optional)
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={2}
                maxLength={MAX_NOTE_LENGTH}
                placeholder="e.g. No longer needed"
              />
            </label>
            <div className="dialog-actions">
              <button type="button" autoFocus onClick={() => setConfirming(false)}>
                Keep it
              </button>
              <button
                type="button"
                className="button-danger"
                onClick={() => {
                  setConfirming(false);
                  cancel.mutate();
                }}
              >
                Cancel shipment
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
